import type { RosterExportBlockerCode, RosterExportView, SupportedLocale } from '@stm/contracts';
import { translate, type TranslationKey } from '@stm/i18n';
import { csvTextCell } from './csv';
import { rosterRoleKeys, rosterStatusKeys } from './matchRoster';
import { eligibilityOutcomeKeys } from './rosterEligibility';

export const rosterExportBlockerKeys: Readonly<Record<RosterExportBlockerCode, TranslationKey>> = {
  PROFILE_NOT_ACTIVE: 'roster.export.blocker.PROFILE_NOT_ACTIVE',
  ROSTER_NOT_FINALIZED: 'roster.export.blocker.ROSTER_NOT_FINALIZED',
  ENTRIES_NOT_VISIBLE: 'roster.export.blocker.ENTRIES_NOT_VISIBLE',
  ROSTER_NOT_ELIGIBLE: 'roster.export.blocker.ROSTER_NOT_ELIGIBLE',
  MANUAL_CHECK_UNRESOLVED: 'roster.export.blocker.MANUAL_CHECK_UNRESOLVED',
};

const rosterExportColumnKeys: readonly TranslationKey[] = [
  'roster.export.column.shirt',
  'roster.export.column.familyName',
  'roster.export.column.givenName',
  'roster.export.column.role',
  'roster.export.column.outcome',
];

export function rosterExportPath(base: string, matchId: string): string {
  return `${base}/matches/${encodeURIComponent(matchId)}/roster/export`;
}

/** The sentence for one blocker; the manual check blocker names the check. */
export function rosterExportBlockerText(
  locale: SupportedLocale,
  blocker: RosterExportView['blockers'][number],
): string {
  return translate(locale, rosterExportBlockerKeys[blocker.code]).replace(
    '{key}',
    blocker.key ?? '',
  );
}

/** A file name that holds no personal data: the match day and whether the file is a draft. */
export function rosterExportFileName(exported: RosterExportView): string {
  return `roster-${exported.matchDay}-${exported.officialReady ? 'ready' : 'draft'}.csv`;
}

/**
 * The export as CSV in the reader's language, with a byte-order mark so spreadsheets read the accents.
 * The first line labels the file an internal aid, then come the match, whether it is official-ready
 * with every reason it is not, a blank line and the rows. Every text cell is safe against formulas.
 */
export function rosterExportCsv(exported: RosterExportView, locale: SupportedLocale): string {
  const text = (key: TranslationKey): string => csvTextCell(translate(locale, key));
  const line = (key: TranslationKey, value: string): string => `${text(key)},${csvTextCell(value)}`;
  const lines = [
    text('roster.export.file.label'),
    line('roster.export.file.competition', exported.competitionName),
    line('roster.export.file.opponent', exported.opponentLabel),
    line('roster.export.file.matchDay', exported.matchDay),
    line('roster.export.file.status', translate(locale, rosterStatusKeys[exported.rosterStatus])),
    line(
      'roster.export.file.officialReady',
      translate(
        locale,
        exported.officialReady ? 'roster.export.file.yes' : 'roster.export.file.no',
      ),
    ),
    ...exported.blockers.map((blocker) =>
      line('roster.export.file.blocker', rosterExportBlockerText(locale, blocker)),
    ),
    '',
    rosterExportColumnKeys.map((key) => text(key)).join(','),
    ...exported.rows.map((row) =>
      [
        row.shirtNumber === null ? '' : String(row.shirtNumber),
        csvTextCell(row.familyName),
        csvTextCell(row.givenName),
        text(rosterRoleKeys[row.role]),
        text(eligibilityOutcomeKeys[row.outcome]),
      ].join(','),
    ),
  ];
  return `\uFEFF${lines.join('\r\n')}\r\n`;
}
